import { Services } from "./services";
import { Money } from "@/generated/graphql/generic";
import { getEnv } from "@/util/env";
import { ProductStateType } from "@/util/constants";
import { Url } from "@/helpers/url";

export class JsonLd {
  values: {
    name: string;
    phone: string;
    email: string;
    url: string;
    logo: string;
  };

  datasets = [] as JsonLdDataset[];
  scripts = new Map<string, HTMLElement>();

  constructor(protected services: Services) {}

  load() {
    const env = getEnv();

    this.values = {
      name: window.location.host,
      phone: env.PUBLIC_ORGANIZATION_PHONE,
      email: this.services.customerChannel.contactEmail,
      url: new URL("/", window.location.origin).toString(),
      logo: this.services.customerChannel.logoUrl ?? "",
    };
  }

  serializeContent(declaration: object) {
    return JSON.stringify(declaration, null, 2);
  }

  createPagePayload(dataset: JsonLdDataset) {
    const values = this.values;

    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: dataset.page!.title,
    };
  }

  createOrganizationPayload(dataset: JsonLdDataset) {
    const values = this.values;

    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: values.name,
      url: values.url,
      logo: values.logo,
      telephone: values.phone,
      email: values.email,
    };
  }

  createSearchActionPayload(dataset: JsonLdDataset) {
    const placeholderSearch = new URLSearchParams({ q: '_query_' });
    const placeholderUrl = Url.to('search-page', {}, placeholderSearch.toString());
    const seachUrlString = placeholderUrl.toString().replace("=_query_", "={query}");
    const name = `${this.values.name.replace('www', '')} part finder`;

    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      url: this.values.url,
      name: name,
      potentialAction: {
        "@type": "SearchAction",
        target: seachUrlString,
        "query-input": "required name=search_term_string",
      },
    };
  }

  createBreadcrumbsPayload(dataset: JsonLdDataset) {
    const items = dataset.breadcrumbs!;
    const indexOffset = 1;
    return {
      "@type": "BreadcrumbList",
      "@context": "https://schema.org",
      itemListElement: items.map((item, index) => {
        return {
          "@type": "ListItem",
          position: index + indexOffset,
          item: {
            "@type": "Thing",
            "@id": item.url,
            name: item.name,
          },
        };
      }),
    };
  }

  createProductListPayload(dataset: JsonLdDataset) {
    const data = dataset.productList!;
    const pageItemsCount = 20;
    const indexOffset = 1 + (data.page - 1) * pageItemsCount;
    return {
      "@type": "ItemList",
      "@context": "https://schema.org",
      url: location.href,
      numberOfItems: data.count,
      itemListElement: data.items.map((item, index) => {
        return {
          "@type": "ListItem",
          position: index + indexOffset,
          url: item.url,
        };
      }),
    };
  }

  createAggregatePayload(dataset: JsonLdDataset) {
    const data = dataset.aggregated!;

    return {
      "@type": "AggregateOffer",
      "@context": "https://schema.org",
      url: location.href,
      lowPrice: data.lowPrice,
      highPrice: data.highPrice,
      priceCurrency: data.priceCurrency,
      numberOfItems: data.offerCount,
    };
  }

  createProductPayload(dataset: JsonLdDataset) {
    const product = dataset.product!;
    const optionalDeclaration = {} as Record<string, string[]>;

    if (product.image) {
      optionalDeclaration["image"] = [product.image];
    }

    const priceValidUntil = this.getNextMonthDate();
    const prices = [];
    if (product.prices?.length) {
      prices.push(...product.prices);
    } else {
      prices.push({
        money: { amount: "0", cents: "0", currency: "EUR" },
        isLocalStockSupplier: false,
        isDummy: true,
      });
    }

    const offers = prices.map((item) => {
      const price = this.calcOfferPrice(item.money);

      return {
        "@type": "Offer",
        availability: "https://schema.org/" + this.calcPriceAvilability(product.state, item),
        itemCondition: "https://schema.org/NewCondition",
        mpn: product.mpn,
        url: product.url,
        price: price.value,
        priceCurrency: price.currency,
        priceValidUntil: priceValidUntil,
      };
    });

    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      sku: product.mpn,
      mpn: product.mpn,
      description: product.description,
      brand: {
        "@type": "Brand",
        name: product.brand,
      },
      manufacturer: product.brand,
      ...optionalDeclaration,
      offers: offers?.[0],
    };
  }

  getNextMonthDate(currentDate = new Date()) {
    currentDate.setMonth(currentDate.getMonth() + 1);

    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  calcPriceAvilability(state: ProductStateType, price: { money: Money; isLocalStockSupplier?: boolean; isDummy?: boolean }): JsonLdProductAvailability {
    if (state === ProductStateType.Active && !price.isDummy) {
      return price.isLocalStockSupplier ? "InStock" : "PreOrder";
    }

    if (state === ProductStateType.Discontinued) {
      return "Discontinued";
    }

    return "OutOfStock";
  }

  calcOfferPrice(value: Money) {
    const formatter = this.services.formatter;
    const amount = Number(value.amount);
    // same value rounding like on product page
    const price = formatter.getCustomerMoneyValue(amount, true);
    const priceValue = formatter.formatMoney(price, "full", true);
    const priceLd = priceValue.replaceAll(" ", "");

    return {
      value: priceLd,
      currency: value.currency,
    };
  }

  registerDataset(item: JsonLdDataset) {
    this.datasets.push(item);
  }

  unregisterDatset(item: JsonLdDataset) {
    this.datasets.splice(this.datasets.indexOf(item), 1);
  }

  getMergedDataset() {
    const result = {} as JsonLdDataset;
    for (const item of this.datasets) {
      result.breadcrumbs = item.breadcrumbs ?? result.breadcrumbs;
      result.organization = item.organization ?? result.organization;
      result.page = item.page ?? result.page;
      result.product = item.product ?? result.product;
      result.search = item.search ?? result.search;
    }

    return result;
  }

  getScriptPayload(key: string, dataset: JsonLdDataset) {
    if (key === "page" && dataset.page) {
      return this.createPagePayload(dataset);
    }

    if (key === "organization" && dataset.organization) {
      return this.createOrganizationPayload(dataset);
    }

    if (key === "search" && dataset.search) {
      return this.createSearchActionPayload(dataset);
    }

    if (key === "breadcrumbs" && dataset.breadcrumbs?.length) {
      return this.createBreadcrumbsPayload(dataset);
    }

    if (key === "product" && dataset.product) {
      return this.createProductPayload(dataset);
    }

    if (key === "productList" && dataset.productList) {
      return this.createProductListPayload(dataset);
    }

    if (key === "aggregated" && dataset.aggregated) {
      return this.createAggregatePayload(dataset);
    }

    return null;
  }

  retriveScript(key: string) {
    if (!this.scripts.has(key)) {
      const script = document.createElement("script");
      script.type = "application/ld+json";

      this.scripts.set(key, script);
    }

    return this.scripts.get(key)!;
  }

  updateScript(key: string, dataset: JsonLdDataset) {
    const parent = document.head;
    const script = this.retriveScript(key);
    const payload = this.getScriptPayload(key, dataset);
    const contains = parent.contains(script);

    if (payload) {
      const content = this.serializeContent(payload);
      if (script.textContent !== content) {
        script.textContent = content;
      }
      if (!contains) {
        parent.appendChild(script);
      }
    } else {
      if (contains) {
        parent.removeChild(script);
      }
      script.textContent = "";
    }
  }

  update() {
    const dataset = this.getMergedDataset();
    this.updateScript("organization", dataset);
    this.updateScript("page", dataset);
    this.updateScript("search", dataset);
    this.updateScript("breadcrumbs", dataset);
    this.updateScript("product", dataset);
    this.updateScript("productList", dataset);
    this.updateScript("aggregated", dataset);
  }
}

export type JsonLdDataset = {
  page?: {
    title: string;
  };
  organization?: boolean;
  search?: boolean;
  product?: {
    id: string;
    mpn: string;
    url: string;
    name: string;
    description: string;
    image?: string;
    brand: string;
    manufacturer: string;
    prices?: { money: Money; isLocalStockSupplier: boolean }[];
    state: ProductStateType;
  };
  aggregated?: {
    lowPrice: string;
    highPrice: string;
    priceCurrency: string;
    offerCount: number;
  };
  productList?: {
    count: number;
    items: { url: string; name: string }[];
    page: number;
  };
  breadcrumbs?: { name: string; url: string }[];
};

type JsonLdProductAvailability = "Discontinued" | "OutOfStock" | "InStock" | "PreOrder";
